import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
} from "@mui/material";
import AdminSideNav from "../../../adminComponents/sidenav";
import AdminHeader from "../../../adminComponents/header";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import FaqManager from "./FaqManager";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HistoryIcon from "@mui/icons-material/History";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";

// Helper functions for date conversion
const formatDateForInput = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateForBackend = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toISOString();
};

const formatDateDisplay = (isoString) => {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Compute preview expiry based on start date + months (for paid) or +3 days (demo)
const computeExpiry = (startDateStr, status, months) => {
  if (!startDateStr || !status) return null;
  const start = new Date(startDateStr);
  if (status === "demo") {
    const d = new Date(start);
    d.setDate(d.getDate() + 3);
    return d;
  }
  if (status === "paid") {
    const d = new Date(start);
    d.setDate(d.getDate() + (parseInt(months) || 1) * 30);
    return d;
  }
  return null;
};

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string().required("Name is required").min(2),
  email: Yup.string().required("Email is required").email("Invalid email format"),
  companyName: Yup.string().required("Company name is required").min(2),
  phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(/^[0-9]+$/, "Must be numeric")
    .min(8),
  industryType: Yup.string().required("Industry type is required"),
  subscriptionStatus: Yup.string().required("Subscription status is required"),
  password: Yup.string().min(6, "Min 6 characters"),
  customerCareNo: Yup.string()
    .matches(/^[0-9]*$/, "Must be numeric")
    .min(8, "Min 8 digits"),
  holdDays: Yup.number().nullable().min(1, "Min 1 day").max(365, "Max 365 days"),
  subscriptionAmount: Yup.number().nullable().min(0, "Amount must be positive"),
});

const subscriptionStatuses = [
  { value: "paid", label: "Paid", color: "success" },
  { value: "demo", label: "Demo", color: "warning" },
  { value: "inactive", label: "In Active", color: "error" },
  { value: "yettostart", label: "Yet To Start", color: "primary" },
];

const monthOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const sectionHeader = (icon, title) => (
  <Grid item xs={12} sx={{ mt: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
      {icon}
      <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", ml: 1 }}>
        {title}
      </Typography>
    </Box>
    <Divider sx={{ mb: 3 }} />
  </Grid>
);

const EditUser = () => {
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [userData, setUserData] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      companyName: "",
      phoneNumber: "",
      industryType: "",
      subscriptionStatus: "",
      password: "",
      customerCareNo: "",
      subscriptionStart: "",
      subscriptionMonths: 1,
      subscriptionAmount: 0,
      // Hold fields (one-time add per save)
      holdDate: "",
      holdDays: "",
      holdReason: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleUpdateUser(values);
    },
  });

  useEffect(() => {
    if (id) fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      setFetchLoading(true);
      const response = await axiosInstance.get(`${apiEndPointsConfig.singlLead}/${id}`);
      const u = response?.data?.data;
      if (u) {
        setUserData(u);
        formik.setValues({
          name: u.name || "",
          email: u.email || "",
          companyName: u.companyName || "",
          phoneNumber: u.phoneNumber?.toString() || "",
          industryType: u.industryType || "",
          subscriptionStatus: u.subscriptionStatus || "",
          password: u?.password || "",
          subscriptionStart: formatDateForInput(u?.subscriptionStart) || "",
          subscriptionMonths: u.subscriptionMonths || 1,
          customerCareNo: u.customerCareNo?.toString() || "",
          subscriptionAmount: u.subscriptionAmount || 0,
          holdDate: "",
          holdDays: "",
          holdReason: "",
        });
      }
    } catch (err) {
      setError("Failed to fetch user details. Please try again.");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleUpdateUser = async (values) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const updateData = {
        userId: id,
        name: values.name,
        email: values.email,
        companyName: values.companyName,
        phoneNumber: parseInt(values.phoneNumber),
        industryType: values.industryType,
        subscriptionStatus: values.subscriptionStatus,
        subscriptionStart: formatDateForBackend(values.subscriptionStart),
        subscriptionMonths: parseInt(values.subscriptionMonths) || 1,
        customerCareNo: values.customerCareNo ? parseInt(values.customerCareNo) : null,
        subscriptionAmount: values.subscriptionAmount ? parseFloat(values.subscriptionAmount) : 0,
      };

      if (values.password) updateData.password = values.password;

      // Hold tracking
      if (values.holdDate && values.holdDays) {
        updateData.holdDate = formatDateForBackend(values.holdDate);
        updateData.holdDays = parseInt(values.holdDays);
        updateData.holdReason = values.holdReason || "";
      }

      const res = await axiosInstance.put(`${apiEndPointsConfig.updateUser}/${id}`, updateData);

      if (res.data?.holdExtended) {
        const newExp = new Date(res.data.newExpiryDate).toLocaleDateString("en-GB", {
          day: "2-digit", month: "short", year: "numeric",
        });
        setSuccess(`✅ User updated. Account was on hold for ${values.holdDays} day(s) — expiry extended to ${newExp}.`);
      } else {
        setSuccess("User updated successfully!");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Re-fetch to show updated history
      fetchUserDetails();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update user. Please try again.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  // Live preview of expiry date
  const previewExpiry = computeExpiry(
    formik.values.subscriptionStart,
    formik.values.subscriptionStatus,
    formik.values.subscriptionMonths
  );

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#fafafa",
      "&:hover": { bgcolor: "#fff" },
    },
  };

  if (fetchLoading) {
    return (
      <Box sx={{ width: "100%" }}>
        <Box sx={{ ml: { xs: "0px", md: "200px" } }}>
          <AdminSideNav />
          <Box sx={{ display: { xs: "none", md: "initial" } }}>
            <AdminHeader title="Edit User" />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "70vh" }}>
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={50} sx={{ color: "#6366F1" }} />
              <Typography sx={{ mt: 2, color: "#666" }}>Loading user details...</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Box sx={{ ml: { xs: "0px", md: "200px" } }}>
        <AdminSideNav />
        <Box sx={{ display: { xs: "none", md: "initial" } }}>
          <AdminHeader title="Edit User" />
        </Box>
        <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 4 } }}>

          {/* Alerts */}
          {(error || success) && (
            <Box sx={{ mb: 3 }}>
              {error && <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 2, mb: 1 }}>{error}</Alert>}
              {success && <Alert severity="success" onClose={() => setSuccess("")} sx={{ borderRadius: 2 }}>{success}</Alert>}
            </Box>
          )}

          {/* ── MAIN EDIT CARD ── */}
          <Card elevation={0} sx={{ mx: "auto", mb: 4, borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
            <Box sx={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)", p: 3, color: "white" }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Edit User Information</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Update user details and manage account settings</Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>

                  {/* ── Personal Information ── */}
                  {sectionHeader(<PersonIcon sx={{ color: "#6366F1" }} />, "Personal Information")}

                  <Grid item xs={12} md={6}>
                    <TextField label="Full Name" name="name" fullWidth variant="outlined" value={formik.values.name}
                      onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name} required sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Email Address" name="email" type="email" fullWidth variant="outlined"
                      value={formik.values.email} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email} required sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Phone Number" name="phoneNumber" fullWidth variant="outlined"
                      value={formik.values.phoneNumber} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                      helperText={formik.touched.phoneNumber && formik.errors.phoneNumber} required sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Password" name="password" fullWidth variant="outlined"
                      placeholder="Leave empty to keep current" value={formik.values.password}
                      onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password ? formik.errors.password : "Leave empty to keep current password"}
                      sx={fieldSx} />
                  </Grid>

                  {/* ── Company Information ── */}
                  {sectionHeader(<BusinessIcon sx={{ color: "#6366F1" }} />, "Company Information")}

                  <Grid item xs={12} md={6}>
                    <TextField label="Company Name" name="companyName" fullWidth variant="outlined"
                      value={formik.values.companyName} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.companyName && Boolean(formik.errors.companyName)}
                      helperText={formik.touched.companyName && formik.errors.companyName} required sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Industry Type" name="industryType" fullWidth variant="outlined"
                      value={formik.values.industryType} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.industryType && Boolean(formik.errors.industryType)}
                      helperText={formik.touched.industryType && formik.errors.industryType} required sx={fieldSx} />
                  </Grid>

                  {/* ── Account Settings ── */}
                  {sectionHeader(<SupportAgentIcon sx={{ color: "#6366F1" }} />, "Account Settings")}

                  {/* Subscription Status */}
                  <Grid item xs={12} md={3}>
                    <TextField select label="Subscription Status" name="subscriptionStatus" fullWidth variant="outlined"
                      value={formik.values.subscriptionStatus} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.subscriptionStatus && Boolean(formik.errors.subscriptionStatus)}
                      helperText={formik.touched.subscriptionStatus && formik.errors.subscriptionStatus} required sx={fieldSx}>
                      {subscriptionStatuses.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {o.label}
                            <Chip label={o.label} size="small" color={o.color} sx={{ height: 20, fontSize: "0.7rem" }} />
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Customer Care No */}
                  <Grid item xs={12} md={3}>
                    <TextField label="Customer Care Number" name="customerCareNo" fullWidth variant="outlined"
                      value={formik.values.customerCareNo} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.customerCareNo && Boolean(formik.errors.customerCareNo)}
                      helperText={formik.touched.customerCareNo && formik.errors.customerCareNo} sx={fieldSx} />
                  </Grid>

                  {/* Process Live Date */}
                  <Grid item xs={12} md={3}>
                    <TextField label="Process Live Date" name="subscriptionStart" type="date" fullWidth variant="outlined"
                      value={formik.values.subscriptionStart} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.subscriptionStart && Boolean(formik.errors.subscriptionStart)}
                      helperText={formik.touched.subscriptionStart && formik.errors.subscriptionStart}
                      InputLabelProps={{ shrink: true }} sx={fieldSx} />
                  </Grid>

                  {/* Subscription Months */}
                  <Grid item xs={12} md={3}>
                    <TextField select label="Subscription Months" name="subscriptionMonths" fullWidth variant="outlined"
                      value={formik.values.subscriptionMonths}
                      onChange={formik.handleChange} onBlur={formik.handleBlur}
                      helperText="Number of months paid" sx={fieldSx}>
                      {monthOptions.map((m) => (
                        <MenuItem key={m} value={m}>{m} {m === 1 ? "Month" : "Months"}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>


                  {/* Subscription Amount */}
                  <Grid item xs={12} md={6}>
                    <TextField label="Subscription Amount" name="subscriptionAmount" type="number" fullWidth variant="outlined"
                      value={formik.values.subscriptionAmount} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.subscriptionAmount && Boolean(formik.errors.subscriptionAmount)}
                      helperText={formik.touched.subscriptionAmount && formik.errors.subscriptionAmount} sx={fieldSx} />
                  </Grid>

                  {/* Expiry Preview */}
                  {previewExpiry && (
                    <Grid item xs={12}>
                      <Box sx={{
                        display: "inline-flex", alignItems: "center", gap: 1,
                        bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 2, px: 2, py: 1
                      }}>
                        <Typography variant="body2" sx={{ color: "#16a34a", fontWeight: 600 }}>
                          📅 Calculated Expiry Date:
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#15803d", fontWeight: 700 }}>
                          {formatDateDisplay(previewExpiry)}
                        </Typography>
                        {formik.values.subscriptionStatus === "paid" && (
                          <Chip label={`${formik.values.subscriptionMonths} month(s)`} size="small"
                            sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 600 }} />
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* ── Hold Section ── */}
                  {sectionHeader(<PauseCircleOutlineIcon sx={{ color: "#f59e0b" }} />, "Add Account Hold")}

                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                      Fill these fields only when you want to record a new hold period. Leave empty if no hold today.
                    </Alert>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField label="Hold Date" name="holdDate" type="date" fullWidth variant="outlined"
                      value={formik.values.holdDate} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      InputLabelProps={{ shrink: true }} sx={fieldSx}
                      helperText="Date account was put on hold" />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField label="Hold Days" name="holdDays" type="number" fullWidth variant="outlined"
                      value={formik.values.holdDays} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.holdDays && Boolean(formik.errors.holdDays)}
                      helperText={formik.touched.holdDays ? formik.errors.holdDays : "Number of days on hold"}
                      inputProps={{ min: 1, max: 365 }} sx={fieldSx} />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField label="Hold Reason" name="holdReason" fullWidth variant="outlined"
                      value={formik.values.holdReason} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      placeholder="e.g. Payment pending" sx={fieldSx} helperText="Optional reason for hold" />
                  </Grid>

                  {/* ── Action Buttons ── */}
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2, pt: 3, borderTop: "1px solid #e0e0e0" }}>
                      <Button variant="outlined" onClick={() => navigate("/admin/users")} disabled={loading}
                        sx={{ borderRadius: 2, textTransform: "capitalize", px: 4, py: 1, color: "#666", borderColor: "#ddd" }}>
                        Cancel
                      </Button>
                      <Button variant="contained" type="submit" disabled={loading}
                        sx={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)", borderRadius: 2, textTransform: "capitalize", px: 4, py: 1, minWidth: 140 }}>
                        {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Save Changes"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>

          {/* ── PROCESS LIVE DATE HISTORY ── */}
          {userData?.processLiveDateHistory?.length > 0 && (
            <Card elevation={0} sx={{ mx: "auto", mb: 4, borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
              <Box sx={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)", p: 3, color: "white" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <HistoryIcon />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>Process Live Date History</Typography>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>All times the process live date was changed</Typography>
              </Box>
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: "#f8f9ff" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Process Live Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Months</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Expiry Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Set By</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Set At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...userData.processLiveDateHistory].reverse().map((h, i) => (
                        <TableRow key={i} sx={{ "&:hover": { bgcolor: "#fafafa" } }}>
                          <TableCell>{userData.processLiveDateHistory.length - i}</TableCell>
                          <TableCell>
                            <Chip label={formatDateDisplay(h.date)} size="small"
                              sx={{ bgcolor: "#ede9fe", color: "#6d28d9", fontWeight: 600 }} />
                          </TableCell>
                          <TableCell>
                            <Chip label={`${h.months} month(s)`} size="small"
                              sx={{ bgcolor: "#dbeafe", color: "#1d4ed8", fontWeight: 600 }} />
                          </TableCell>
                          <TableCell>
                            <Chip label={formatDateDisplay(h.expiryDate)} size="small"
                              sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 600 }} />
                          </TableCell>
                          <TableCell>{h.setBy || "—"}</TableCell>
                          <TableCell sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                            {formatDateDisplay(h.setAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* ── HOLD HISTORY ── */}
          {userData?.holdHistory?.length > 0 && (
            <Card elevation={0} sx={{ mx: "auto", mb: 4, borderRadius: 3, border: "1px solid #fde68a", overflow: "hidden" }}>
              <Box sx={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", p: 3, color: "white" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PauseCircleOutlineIcon />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>Hold History</Typography>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>All account hold periods recorded</Typography>
              </Box>
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: "#fffbeb" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Hold Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Hold Days</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Expiry Change</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Added By</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Added At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...userData.holdHistory].reverse().map((h, i) => (
                        <TableRow key={i} sx={{ "&:hover": { bgcolor: "#fafafa" }, bgcolor: h.expiryExtended ? "#fffbeb" : "inherit" }}>
                          <TableCell>{userData.holdHistory.length - i}</TableCell>
                          <TableCell>
                            <Chip label={formatDateDisplay(h.holdDate)} size="small"
                              sx={{ bgcolor: "#fef3c7", color: "#d97706", fontWeight: 600 }} />
                          </TableCell>
                          <TableCell>
                            <Chip label={`${h.holdDays} day(s)`} size="small"
                              sx={{ bgcolor: "#fee2e2", color: "#dc2626", fontWeight: 600 }} />
                          </TableCell>
                          <TableCell sx={{ color: "#374151" }}>{h.reason || "—"}</TableCell>
                          <TableCell>
                            {h.expiryExtended ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: "#94a3b8", textDecoration: "line-through" }}>
                                  {formatDateDisplay(h.expiryBefore)}
                                </Typography>
                                <Chip label={`→ ${formatDateDisplay(h.expiryAfter)}`} size="small"
                                  sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 700 }} />
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: "#64748b" }}>No extension</Typography>
                            )}
                          </TableCell>
                          <TableCell>{h.addedBy || "—"}</TableCell>
                          <TableCell sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                            {formatDateDisplay(h.addedAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* ── FAQ Manager ── */}
          <Card elevation={0} sx={{ mx: "auto", borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
            <Box sx={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)", p: 3, color: "white" }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>FAQ Management</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Manage frequently asked questions for this user</Typography>
            </Box>
            <CardContent sx={{ p: 4 }}>
              <FaqManager />
            </CardContent>
          </Card>

        </Box>
      </Box>
    </Box>
  );
};

export default EditUser;
